import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const SignedDeclarationTab = ({ declarationFilePath, registrationId }) => {
  const [loading, setLoading] = useState(true);
  const [declarationUrl, setDeclarationUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDeclaration = async () => {
      if (!declarationFilePath) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Extract file name from path
        const fileName = declarationFilePath.replace('signed-declarations/', '');
        
        // Generate signed URL for admins (RLS allows admin access)
        const { data: urlData, error: urlError } = await supabase.storage
          .from('signed-declarations')
          .createSignedUrl(fileName, 3600); // 1 hour expiration

        if (urlError) {
          console.error('Error creating signed URL:', urlError);
          setError('Failed to load declaration document');
          return;
        }

        setDeclarationUrl(urlData?.signedUrl || null);
      } catch (err) {
        console.error('Error loading declaration:', err);
        setError('Failed to load declaration document');
      } finally {
        setLoading(false);
      }
    };

    loadDeclaration();
  }, [declarationFilePath]);

  const handleView = () => {
    if (declarationUrl) {
      window.open(declarationUrl, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!declarationUrl) return;

    try {
      // Fetch the file as a blob
      const response = await fetch(declarationUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Extract file name from path
      const fileName = declarationFilePath?.split('/').pop() || `declaration_${registrationId}.pdf`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading declaration:', error);
      alert('Failed to download declaration. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading declaration...</p>
        </div>
      </div>
    );
  }

  if (!declarationFilePath) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <div className="bg-yellow-50 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Declaration Uploaded</h3>
          <p className="text-gray-600">
            The signed declaration document has not been uploaded for this registration yet.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <div className="bg-red-50 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Declaration</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-100 rounded-lg p-3">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Signed Declaration Document</h3>
            <p className="text-sm text-gray-600 mb-4">
              View or download the signed declaration document for this registration.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleView}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                View Document
              </Button>
              <Button
                onClick={handleDownload}
                className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded PDF Viewer */}
      {declarationUrl && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <iframe
            src={declarationUrl}
            className="w-full h-[800px]"
            title="Signed Declaration Document"
          />
        </div>
      )}
    </div>
  );
};

export default SignedDeclarationTab;

